/* global performance store */

import { exec } from 'child_process'
import { homedir } from 'os'
import { resolve, sep } from 'path'
import { promisify } from 'util'
import color from 'color'
import crc32 from 'crc/crc32'
import emojiRegex from 'emoji-regex/es2015'
import emojiUnicode from 'emoji-unicode'
import fs from 'fs'
import glob from 'glob'
import untildify from 'untildify'

const root = `${homedir()}${sep}.hyper-touchbar-switcher`
const stat = promisify(fs.stat)
const mkdir = promisify(fs.mkdir)
const readFile = promisify(fs.readFile)
const writeFile = promisify(fs.writeFile)

export const noop = () => {}

export let configuration = {
  backgroundColor: '',
  click: noop,
  colourEffect: {
    lighten: 0.5,
  },
  icon: resolve(__dirname, '..', 'assets', 'Hyper@2x.png'),
  label: '',
  iconPosition: 'left',
  tabs: new Map(),
}

export const options = {
  cache: null,
  statCache: null,
  ignore: ['**/.git', '**/.svn', '**/.hg', '**/node_modules/**', '**/bower_components/**', '**/tmp/**', '**/temp/**'],
  silent: true,
  strict: false,
}

export const init = async () => {
  try {
    await stat(root)
  } catch {
    await mkdir(root)
  }
}

export const digest = config => {
  const sort = config =>
    Object.fromEntries(
      Object.entries(config)
        .map(([k, v]) => (typeof v === 'object' ? [k, sort(v)] : [k, v]))
        .sort(),
    )

  const flatten = config =>
    JSON.stringify(sort(config))
      .replace(/\s/g, '')
      .split('')
      .map(char => String.prototype.codePointAt.apply(char))
      .join()

  return crc32(flatten(sort(config))).toString(4)
}

export const activate = uid => {
  store.dispatch({
    type: 'SESSION_SET_ACTIVE',
    uid,
  })
}

export const build = config => {
  try {
    const { colour, colourEffect, directory, icon, iconPosition, isActive, label, session: uid, title } = {
      ...config,
      ...configuration,
      ...configuration.tabs.get(config.directory),
    }

    return {
      icon: iconographer(icon),
      label: labeller(directory, label, title),
      backgroundColor: highlighter(colour, colourEffect, isActive),
      click: activate.bind(null, uid),
      iconPosition,
      uid,
    }
  } catch {
    return {
      icon: configuration.icon,
      label: configuration.label,
      backgroundColor: configuration.backgroundColor,
      click: configuration.click,
      iconPosition: configuration.iconPosition,
      uid: '',
    }
  }
}

export const highlighter = (colour, colourEffect, isActive) => {
  try {
    if (isActive) {
      return colour
    } else {
      const name = Object.keys(colourEffect)[0]
      const value = colourEffect[name]
      const context = color(colour)

      return Reflect.get(context, name)
        .call(context, value)
        .hex()
    }
  } catch {
    return colour
  }
}

export const iconographer = icon => {
  try {
    const match = emojiRegex().exec(icon)

    if (match) {
      const codePoint = emojiUnicode(match[0])

      return resolve(__dirname, '..', 'assets', 'twemoji', `${codePoint}.png`)
    }

    return untildify(icon)
  } catch {
    return untildify(icon)
  }
}

export const decoder = (key, config) => {
  return new Promise(resolve => {
    try {
      const { cache, statCache } = glob(untildify(`${key}/`), options, (_, matches) =>
        resolve(new Map(matches.map(match => [match.slice(0, -1), config]))),
      )

      options.cache = cache
      options.statCache = statCache
    } catch {
      resolve(new Map())
    }
  })
}

export const path = pid => {
  try {
    return new Promise(resolve =>
      exec(`lsof -p ${pid} | awk '$4=="cwd"' | tr -s ' ' | cut -d ' ' -f9-`, (_, stdout) => resolve(stdout.trim())),
    )
  } catch {
    return Promise.resolve('')
  }
}

export const expander = async config => {
  const start = performance.now()

  const cacheFile = digest(config.tabs)
  const cachePath = `${root}${sep}${cacheFile}`

  const cacheHit = async () => {
    const results = await readFile(cachePath)

    const tabs = new Map(JSON.parse(results))

    configuration = {
      ...configuration,
      tabs,
    }

    const end = performance.now()

    console.log(`${configuration.tabs.size} paths loaded from cache in ${(end - start) * 0.001}`)
  }

  const cacheMiss = async () => {
    const results = await Promise.all(Object.entries(config.tabs).map(([key, config]) => decoder(key, config)))

    const tabs = flatten(results)

    configuration = {
      ...configuration,
      raw: config.tabs,
      tabs,
    }

    await writeFile(cachePath, JSON.stringify(Array.from(tabs.entries()))) // eslint-disable-line unicorn/prefer-spread

    const end = performance.now()

    console.log(`${configuration.tabs.size} paths found in ${(end - start) * 0.001}`)
  }

  let cacheFound

  try {
    cacheFound = await stat(cachePath)
  } catch {
    cacheFound = false
  }

  if (cacheFound) {
    await cacheHit()
  } else {
    await cacheMiss()
  }
}

export const flatten = results => {
  try {
    return results.reduce((accumulator, current) => new Map([...accumulator, ...current]), new Map())
  } catch {
    return new Map()
  }
}

export const labeller = (directory, label, title) => {
  switch (label) {
    case 'none':
      return ''
    case 'path':
      return directory
    case 'directory':
      return directory.slice(directory.lastIndexOf(sep) + 1)
    case 'title':
      return title
    default:
      return title
  }
}
